import React, { useState, useRef, useEffect } from 'react';
import { useMap, useMapsLibrary } from '@vis.gl/react-google-maps';
import { Search, AlertTriangle } from 'lucide-react';

const PlacesAutocomplete = ({
  onPlaceSelect,
  placeholder = "Enter your business name and location...",
  className = "search-input",
  types = ['establishment']
}) => {
  const [inputValue, setInputValue] = useState('');
  const autocompleteRef = useRef(null);
  const placeAutocompleteElementRef = useRef(null);

  const handlePlaceSelect = async ({ placePrediction }) => {
    try {
      const place = placePrediction.toPlace();
      await place.fetchFields({
        fields: [
          'displayName',
          'formattedAddress',
          'location',
          'id',
          'nationalPhoneNumber',
          'websiteURI',
          'rating',
          'userRatingCount',
          'photos',
          'businessStatus',
          'types',
          'priceLevel',
          'regularOpeningHours'
        ]
      });

      console.debug('Selected place:', place);

      const businessInfo = {
        name: place.displayName || '',
        address: place.formattedAddress || '',
        location: {
          lat: place.location?.lat || 0,
          lng: place.location?.lng || 0
        },
        placeId: place.id || '',
        phoneNumber: place.nationalPhoneNumber || '',
        website: place.websiteURI || '',
        rating: place.rating || 0,
        userRatingsTotal: place.userRatingCount || 0,
        photos: place.photos || [],
        businessStatus: place.businessStatus || '',
        types: place.types || [],
        priceLevel: place.priceLevel,
        openingHours: place.regularOpeningHours || null
      };

      setInputValue(`${businessInfo.name} - ${businessInfo.address}`);

      if (onPlaceSelect) {
        onPlaceSelect(businessInfo);
      }
    } catch (error) {
      console.error('Error fetching place details:', error);
    }
  };

  const handleInputChange = (value) => {
    setInputValue(value);
  };

  const apiKey = import.meta.env.VITE_GOOGLE_MAPS_API_KEY;
  const placesLib = useMapsLibrary('places');

  useEffect(() => {
    const initializeAutocomplete = async () => {
      if (!apiKey || apiKey === 'your_api_key_here' || !placesLib) {
        return;
      }

      try {
        const placeAutocomplete = new placesLib.PlaceAutocompleteElement({
          includedPrimaryTypes: types,
          componentRestrictions: { country: 'us' }
        });

        placeAutocomplete.style.width = '100%';
        placeAutocomplete.style.border = '2px solid #E5E7EB';
        placeAutocomplete.style.borderRadius = '12px';
        placeAutocomplete.style.backgroundColor = '#ffffff';
        placeAutocomplete.style.colorScheme = 'light';

        placeAutocomplete.placeholder = placeholder;

        const style = document.createElement('style');
        style.textContent = `
          gmp-place-autocomplete-element {
            font-family: inherit;
            color: #111827;
          }
          gmp-place-autocomplete-element::part(prediction-list) {
            background-color: #ffffff;
            border: 1px solid #E5E7EB;
            border-radius: 8px;
            box-shadow: 0 4px 6px -1px rgb(0 0 0 / 0.1);
            z-index: 100;
            position: relative;
          }
          gmp-place-autocomplete-element::part(prediction-item) {
            padding: 12px 16px;
            color: #111827;
            background-color: #ffffff;
            border-bottom: 1px solid #F3F4F6;
          }
          gmp-place-autocomplete-element::part(prediction-item-selected) {
            background-color: #F3F4F6;
            color: #111827;
          }
          gmp-place-autocomplete-element::part(prediction-item-main-text) {
            color: #111827;
            font-weight: 500;
          }
          gmp-place-autocomplete-element::part(prediction-item-match) {
            color: #374151;
            font-weight: 600;
          }
        `;
        document.head.appendChild(style);

        placeAutocomplete.addEventListener('gmp-select', (event) => {
          handlePlaceSelect(event);
        });

        placeAutocompleteElementRef.current = placeAutocomplete;
        if (autocompleteRef.current) {
          autocompleteRef.current.innerHTML = '';
          autocompleteRef.current.appendChild(placeAutocomplete);
        }
      } catch (error) {
        console.error('Error initializing Google Places Autocomplete:', error);
      }
    };

    initializeAutocomplete();

    return () => {
      if (autocompleteRef.current) {
        autocompleteRef.current.innerHTML = '';
      }
    };
  }, [apiKey, placesLib, types, placeholder]);

  if (!apiKey || apiKey === 'your_api_key_here') {
    return (
      <div className="search-input-container relative">
        <div className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">
          <Search className="size-4" />
        </div>
        <input
          type="text"
          className={className}
          placeholder="Please add your Google Maps API key to .env file"
          value={inputValue}
          onChange={(e) => handleInputChange(e.target.value)}
          disabled
        />
        <div className="absolute top-full left-0 right-0 mt-1 bg-amber-50 border border-amber-200 rounded p-2 text-sm text-amber-800 flex items-center gap-2">
          <AlertTriangle className="size-4" />
          Google Maps API key required. Add VITE_GOOGLE_MAPS_API_KEY to your .env file.
        </div>
      </div>
    );
  }

  return (
    <div className="search-input-container">
      <div ref={autocompleteRef} className="flex-1"></div>
    </div>
  );
};

export default PlacesAutocomplete;
